import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Map, Calendar, RefreshCw, CheckCircle2, ArrowLeftRight, Clock, Target, Home,
  PlayCircle, FileText, Link as LinkIcon, ExternalLink, X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

/**
 * The Journey — student education center.
 *
 * Backend-driven: fetches phases + steps + current user's progress from /api/journey.
 * Students mark steps complete/incomplete; progress is persisted.
 */

export default function Journey() {
  const { user } = useAuth();
  const [phases, setPhases] = useState([]);
  const [activePhaseId, setActivePhaseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openStep, setOpenStep] = useState(null);

  const loadJourney = async () => {
    try {
      const { data } = await api.get("/journey");
      setPhases(data);
      setActivePhaseId((curr) => curr ?? (data[0]?.id || null));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load The Journey");
    }
  };

  useEffect(() => {
    setLoading(true);
    loadJourney().finally(() => setLoading(false));
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    await loadJourney();
    setRefreshing(false);
  };

  const totals = useMemo(() => {
    const totalSteps = phases.reduce((sum, p) => sum + (p.steps?.length || 0), 0);
    const completedSteps = phases.reduce(
      (sum, p) => sum + (p.steps?.filter((s) => s.status === "complete").length || 0), 0,
    );
    const completedPhases = phases.filter(
      (p) => (p.steps?.length || 0) > 0 && p.steps.every((s) => s.status === "complete"),
    ).length;
    return { totalSteps, completedSteps, completedPhases };
  }, [phases]);

  const activePhase = phases.find((p) => p.id === activePhaseId);
  const progressPct = totals.totalSteps
    ? Math.round((totals.completedSteps / totals.totalSteps) * 100)
    : 0;

  const toggleStep = async (step) => {
    const wasComplete = step.status === "complete";
    // Optimistic update
    setPhases((prev) =>
      prev.map((p) => ({
        ...p,
        steps: p.steps.map((s) =>
          s.id === step.id ? { ...s, status: wasComplete ? "pending" : "complete" } : s,
        ),
      })),
    );
    try {
      if (wasComplete) {
        await api.delete(`/journey/steps/${step.id}/complete`);
      } else {
        await api.post(`/journey/steps/${step.id}/complete`);
      }
    } catch (e) {
      toast.error("Couldn't save progress — reverting");
      loadJourney();
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="journey-page">
        <div className="flex items-center justify-center py-20">
          <span className="spinner" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="journey-page">
      <JourneyHeader isAdmin={user?.is_admin} onRefresh={refresh} refreshing={refreshing} />
      <ProgressBar
        completedSteps={totals.completedSteps}
        totalSteps={totals.totalSteps}
        completedPhases={totals.completedPhases}
        totalPhases={phases.length}
        pct={progressPct}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 mt-8">
        <PhaseList phases={phases} activeId={activePhaseId} onSelect={setActivePhaseId} />
        <PhaseSteps phase={activePhase} onToggle={toggleStep} onOpen={setOpenStep} />
      </div>

      {openStep && <StepModal step={openStep} onClose={() => setOpenStep(null)} onToggle={toggleStep} />}
    </div>
  );
}

function JourneyHeader({ isAdmin, onRefresh, refreshing }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap mb-7">
      <div className="flex items-start gap-4">
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
          style={{ background: "var(--lime-glow)", color: "var(--lime)" }}
        >
          <Map size={22} strokeWidth={1.6} />
        </div>
        <div>
          <div className="section-tag mb-2"><span className="dot" /> Learn</div>
          <h1 className="h-display text-3xl sm:text-4xl">The Journey</h1>
          <p className="text-sm mt-2 max-w-lg" style={{ color: "var(--text-muted)" }}>
            Navigate your personalized learning phases and track your progress through wholesaling.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm transition hover:bg-white/5 disabled:opacity-50"
          style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
          data-testid="journey-refresh-top"
          title="Reload phases"
        >
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} />
        </button>
        {isAdmin && (
          <Link
            to="/admin/journey"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition hover:bg-white/5"
            style={{ color: "var(--lime)", border: "1px solid var(--lime)" }}
            data-testid="journey-manage-link"
          >
            Manage
          </Link>
        )}
        <Link
          to="/dashboard"
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm transition hover:bg-white/5"
          style={{ color: "var(--text-muted)", border: "1px solid var(--border)" }}
          data-testid="journey-back-link"
        >
          <Home size={14} /> Dashboard
        </Link>
      </div>
    </div>
  );
}

function ProgressBar({ completedSteps, totalSteps, completedPhases, totalPhases, pct }) {
  return (
    <div className="surface-card p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Target size={14} style={{ color: "var(--lime)" }} />
          <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
            Overall Progress
          </span>
        </div>
        <div className="font-mono text-xs" style={{ color: "var(--text-muted)" }}>
          {completedSteps}/{totalSteps} steps · {completedPhases}/{totalPhases} phases
        </div>
      </div>
      <div className="relative h-2 rounded-full overflow-hidden" style={{ background: "var(--surface2)" }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${pct}%`, background: "var(--lime)" }}
        />
      </div>
      <div className="text-right mt-2 font-mono text-xs" style={{ color: "var(--lime)" }}>
        {pct}%
      </div>
    </div>
  );
}

function PhaseList({ phases, activeId, onSelect }) {
  return (
    <div className="surface-card p-4">
      <div className="flex items-center gap-2 mb-4 px-1">
        <Calendar size={14} style={{ color: "var(--lime)" }} />
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color: "var(--text)" }}>
          Learning Phases
        </span>
      </div>
      <div className="flex flex-col gap-2 max-h-[680px] overflow-y-auto pr-1">
        {phases.map((p, idx) => {
          const isActive = p.id === activeId;
          const total = p.steps?.length || 0;
          const completed = p.steps?.filter((s) => s.status === "complete").length || 0;
          const allDone = total > 0 && completed === total;
          const hasActivity = completed > 0;
          return (
            <button
              key={p.id}
              onClick={() => onSelect(p.id)}
              className="text-left p-4 rounded-2xl transition relative shrink-0"
              style={{
                background: isActive ? "var(--lime-glow)" : "var(--surface2)",
                border: isActive ? "1px solid var(--lime)" : "1px solid var(--border)",
              }}
              data-testid={`journey-phase-${idx + 1}`}
            >
              <div className="font-mono text-[10px] uppercase tracking-wider mb-1" style={{ color: isActive ? "var(--lime)" : "var(--text-dim)" }}>
                Phase {idx + 1}
              </div>
              <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                {p.title}
              </div>
              {total > 0 && (
                <div className="font-mono text-[10px] mt-1" style={{ color: "var(--text-dim)" }}>
                  {completed}/{total} steps
                </div>
              )}
              {(hasActivity || isActive) && (
                <span
                  className="absolute top-3 right-3 w-2 h-2 rounded-full"
                  style={{ background: allDone ? "#22c55e" : "var(--lime)" }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PhaseSteps({ phase, onToggle, onOpen }) {
  if (!phase) return null;
  const steps = phase.steps || [];
  return (
    <div>
      <h2 className="h-display text-2xl mb-1">{phase.title}</h2>
      <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>
        {phase.description || "Complete these action steps to unlock your next milestone."}
      </p>

      {steps.length === 0 ? (
        <EmptyPhase />
      ) : (
        <div className="flex flex-col gap-3">
          {steps.map((s, i) => (
            <StepCard key={s.id} step={s} index={i + 1} onToggle={onToggle} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyPhase() {
  return (
    <div className="surface-card p-10 text-center">
      <div
        className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
        style={{ background: "var(--surface2)", color: "var(--text-muted)" }}
      >
        <Calendar size={18} />
      </div>
      <h3 className="font-semibold mb-1">Content coming soon</h3>
      <p className="text-sm" style={{ color: "var(--text-muted)" }}>
        Salvador is building this phase. Check back shortly.
      </p>
    </div>
  );
}

function contentTypeIcon(type) {
  if (type === "video") return PlayCircle;
  if (type === "doc") return FileText;
  if (type === "link") return LinkIcon;
  return FileText;
}

function StepCard({ step, index, onToggle, onOpen }) {
  const complete = step.status === "complete";
  const cardStyle = complete
    ? { background: "rgba(34, 197, 94, 0.06)", border: "1px solid rgba(34, 197, 94, 0.3)" }
    : { background: "var(--surface)", border: "1px solid var(--border)" };
  const Icon = contentTypeIcon(step.content_type);
  const completedAt = step.completed_at ? new Date(step.completed_at).toLocaleDateString() : null;
  return (
    <div className="p-5 rounded-2xl flex items-start gap-4" style={cardStyle} data-testid={`journey-step-${step.id}`}>
      <div className="flex flex-col items-center gap-1 shrink-0">
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center font-mono text-sm"
          style={{
            background: complete ? "rgba(34, 197, 94, 0.15)" : "var(--surface2)",
            color: complete ? "#22c55e" : "var(--text-muted)",
            border: complete ? "1px solid rgba(34, 197, 94, 0.4)" : "1px solid var(--border)",
          }}
        >
          {complete ? <CheckCircle2 size={16} /> : index}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center flex-wrap gap-2 mb-1">
          <h4 className="text-base font-semibold" style={{ color: complete ? "#22c55e" : "var(--text)" }}>
            {step.title}{step.emoji ? ` ${step.emoji}` : ""}
          </h4>
          <Pill icon={Icon}>{step.content_type}</Pill>
          {step.minutes ? <Pill icon={Clock}>{step.minutes} min</Pill> : null}
          {complete && <PillSuccess>✓ Complete</PillSuccess>}
        </div>
        {step.prereq_step_id && (
          <div className="flex items-center gap-1.5 text-xs mt-1" style={{ color: "var(--lime)" }}>
            <ArrowLeftRight size={11} />
            <span>Prerequisite required</span>
          </div>
        )}
        <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
          {complete && completedAt ? `Completed · ${completedAt}` : "Pending"}
        </div>
      </div>

      <div className="flex flex-col gap-2 shrink-0">
        <button
          onClick={() => onOpen(step)}
          className="px-4 py-2 rounded-xl text-sm font-semibold transition"
          style={{ background: "var(--lime)", border: "none", color: "#0c0c0c" }}
          data-testid={`journey-view-step-${step.id}`}
        >
          View Step
        </button>
        <button
          onClick={() => onToggle(step)}
          className="px-4 py-2 rounded-xl text-sm transition"
          style={complete
            ? { background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }
            : { background: "transparent", border: "1px solid #22c55e", color: "#22c55e" }}
          data-testid={`journey-toggle-step-${step.id}`}
        >
          {complete ? "Undo" : "Mark Done"}
        </button>
      </div>
    </div>
  );
}

function StepModal({ step, onClose, onToggle }) {
  const complete = step.status === "complete";
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
      data-testid="journey-step-modal"
    >
      <div
        className="surface-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl hover:bg-white/5 transition"
          style={{ color: "var(--text-muted)" }}
          data-testid="journey-step-modal-close"
        >
          <X size={18} />
        </button>

        <h3 className="h-display text-2xl pr-10 mb-3">
          {step.title}{step.emoji ? ` ${step.emoji}` : ""}
        </h3>
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <Pill>{step.content_type}</Pill>
          {step.minutes ? <Pill icon={Clock}>{step.minutes} min</Pill> : null}
          {complete && <PillSuccess>✓ Complete</PillSuccess>}
        </div>

        <StepBody step={step} />

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={() => onToggle(step)}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold transition"
            style={complete
              ? { background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }
              : { background: "var(--lime)", border: "none", color: "#0c0c0c" }}
            data-testid="journey-modal-toggle"
          >
            {complete ? "Mark as Pending" : "Mark Complete"}
          </button>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl text-sm transition"
            style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--text-muted)" }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function StepBody({ step }) {
  if (step.content_type === "video" && step.video_url) {
    return (
      <div className="aspect-video rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
        <iframe
          src={toEmbed(step.video_url)}
          title={step.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  if (step.content_type === "link" && step.external_url) {
    return (
      <a
        href={step.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition"
        style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--lime)" }}
      >
        <ExternalLink size={14} /> Open Resource
      </a>
    );
  }
  if (step.content_type === "doc" && step.external_url) {
    return (
      <a
        href={step.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition"
        style={{ background: "var(--lime-glow)", color: "var(--lime)", border: "1px solid var(--lime)" }}
      >
        <FileText size={14} /> Open Document
      </a>
    );
  }
  if (step.content_md) {
    return (
      <div className="prose prose-invert max-w-none text-sm whitespace-pre-wrap" style={{ color: "var(--text)" }}>
        {step.content_md}
      </div>
    );
  }
  return (
    <p className="text-sm" style={{ color: "var(--text-muted)" }}>
      No content yet — Salvador is working on it.
    </p>
  );
}

function toEmbed(url) {
  if (!url) return "";
  // YouTube watch / short URL → embed
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

function Pill({ children, icon: Icon }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono"
      style={{ background: "rgba(96, 165, 250, 0.12)", color: "#60a5fa" }}
    >
      {Icon ? <Icon size={10} /> : null}
      {children}
    </span>
  );
}

function PillSuccess({ children }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono"
      style={{ background: "rgba(34, 197, 94, 0.15)", color: "#22c55e" }}
    >
      {children}
    </span>
  );
}
